import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_SCAN_SOUND_ID } from '../utils/pda/scanSoundOptions';
import { getMyPdaPreference } from '../graphql/queries/pdaScanSettingQueries';
import { updateMyScanSetting, UpdateScanSettingInput } from '../graphql/mutations/pdaScanSetting';

const STORAGE_KEY = 'pda-scan-sound-settings-storage';

// 记录上次写入 Native 的 master 值，相等时跳过 bridge 调用
let lastSyncedMaster: boolean | null = null;

// 同步 master 状态到 Native：master=true 关 SDK 三开关；master=false 不动 SDK（用户自己去 iScanPlus 开）
// bridge 保持 4 参数签名向后兼容，子参数当前 Android 端忽略
const syncMasterToNative = (masterEnabled: boolean) => {
  if (lastSyncedMaster === masterEnabled) return;
  try {
    window.PdaApp?.syncSdkScanFeedback?.(masterEnabled, false, false, false);
    lastSyncedMaster = masterEnabled;
  } catch (error) {
    console.error('[ScanSoundSettingsStore] syncSdkScanFeedback 失败:', error);
  }
};

interface ScanSoundSettingsState {
  masterEnabled: boolean;
  vibrateEnabled: boolean;
  breathingLightEnabled: boolean;
  soundEnabled: boolean;
  selectedSoundId: string;

  setSelectedSoundId: (id: string) => void;
  // 登录后从后端拉配置，写入 store + 本地缓存
  loadFromBackend: () => Promise<void>;
  // 改任一项：本地乐观更新立即生效 + 异步写后端
  updateSetting: (patch: UpdateScanSettingInput) => void;
  resetAllSettings: () => void;
}

const DEFAULTS = {
  masterEnabled: false,
  vibrateEnabled: false,
  breathingLightEnabled: false,
  soundEnabled: false,
  selectedSoundId: DEFAULT_SCAN_SOUND_ID,
};

export const useScanSoundSettingsStore = create<ScanSoundSettingsState>()(
  persist(
    (set, get) => ({
      ...DEFAULTS,

      setSelectedSoundId: (id: string) => get().updateSetting({ soundId: id }),

      loadFromBackend: async () => {
        try {
          const preference = await getMyPdaPreference();
          const setting = preference.scanSetting;
          if (setting) {
            set({
              masterEnabled: setting.masterEnabled,
              vibrateEnabled: setting.vibrateEnabled,
              breathingLightEnabled: setting.breathingLightEnabled,
              soundEnabled: setting.soundEnabled,
              selectedSoundId: setting.soundId,
            });
            syncMasterToNative(setting.masterEnabled);
          }
        } catch (error) {
          console.error('[ScanSoundSettingsStore] 拉取后端配置失败:', error);
        }
      },

      updateSetting: (patch: UpdateScanSettingInput) => {
        const next: Partial<ScanSoundSettingsState> = {};
        if (patch.masterEnabled !== undefined) next.masterEnabled = patch.masterEnabled;
        if (patch.vibrateEnabled !== undefined) next.vibrateEnabled = patch.vibrateEnabled;
        if (patch.breathingLightEnabled !== undefined) next.breathingLightEnabled = patch.breathingLightEnabled;
        if (patch.soundEnabled !== undefined) next.soundEnabled = patch.soundEnabled;
        if (patch.soundId !== undefined) next.selectedSoundId = patch.soundId;
        set(next);
        if (patch.masterEnabled !== undefined) {
          syncMasterToNative(patch.masterEnabled);
        }
        void updateMyScanSetting(patch).catch((error) => {
          console.error('[ScanSoundSettingsStore] 同步后端配置失败:', error);
        });
      },

      resetAllSettings: () => {
        lastSyncedMaster = null;
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
          console.error('[ScanSoundSettingsStore] 清除 localStorage 时出错:', error);
        }
        set({ ...DEFAULTS });
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        masterEnabled: state.masterEnabled,
        vibrateEnabled: state.vibrateEnabled,
        breathingLightEnabled: state.breathingLightEnabled,
        soundEnabled: state.soundEnabled,
        selectedSoundId: state.selectedSoundId,
      }),
    }
  )
);
